
import React, { PropsWithChildren, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import CardBenefitModal from './components/CardBenefitModal';
import './ProductPageBenefitsCard.css';
import { Row, Col, Title, Image, Section, Icon, IconChevron } from '../../importCL';

/* eslint-disable */
enum Version {
    Icon = 'icon',
    Image = 'image'
}
/* eslint-enable */

interface IBenefitItem {
    title?: string;
    subTitle?: string;
    imgPath?: string;
    imgAltText?: string;
    iconUuid?: string;
    iconPath?: string;
    iconAltText?: string;
}

interface IProductPageBenefitsCardProps extends MappedComponentProperties {
    version: Version;
    title?: string;
    subTitle?: string;
    boxShadow?: boolean;
    benefits?: IBenefitItem[];
    topMargin?: string;
    bottomMargin?: string;
    grayBackground?: boolean;
    fluidContainer?: boolean;
    newDesign?: boolean;
}

const iconClasses = {
    iconSize: {
        new: 'ProductPageBenefits__iconSize',
        legacy: 'ProductPageBenefits__iconSize--legacy',
    },
    iconVersion: {
        new: 'ProductPageBenefits__iconVersion',
        legacy: 'ProductPageBenefits__iconVersion--legacy',
    },
    benefitItemColumn: {
        new: 'ProductPageBenefits__benefitItemColumn',
        legacy: 'ProductPageBenefits__benefitItemColumn--legacy',
    },
    benefitItem: {
        new: 'ProductPageBenefits__benefitItem',
        legacy: 'ProductPageBenefits__benefitItem--legacy',
    },
    itemTitle: {
        new: 'ProductPageBenefits__itemTitle',
        legacy: 'ProductPageBenefits__itemTitle--legacy',
    },
    section: {
        new: 'ProductPageBenefitsIcon',
        legacy: 'ProductPageBenefits',
    },
    container: {
        new: 'ProductPageBenefits__iconContainer',
        legacy: 'ProductPageBenefits__imageContainer',
    }
};

export const ProductPageBenefitsCardEditConfig = {
    emptyLabel: 'Product Page BenefitsCard',
    isEmpty: (props: PropsWithChildren<IProductPageBenefitsCardProps>) => {
        return !props || !props.version;
    },
};


const ProductPageBenefitsCard = (props: PropsWithChildren<IProductPageBenefitsCardProps>): JSX.Element => {
    const boxShadowClassImage = (props.version === Version.Image || !props.newDesign) && props.boxShadow ? 'ProductPageBenefits__boxShadow' : '';
    const boxShadowClassIcon = props.version === Version.Icon && props.newDesign && props.boxShadow ? 'ProductPageBenefits__benefitItem--box-shadow' : '';
    const iconClassIndex = props.newDesign ? 'new' : 'legacy';
    const grayBackgroundClass = props.grayBackground ? 'gray-bg' : '';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const getBenefitItem = (item: IBenefitItem) => {
        return props.version === Version.Image ?
            <Image loading='lazy' src={item.imgPath} alt={item.imgAltText} /> :
            <Icon loading='lazy' className={iconClasses.iconSize[iconClassIndex]} alt={item.iconAltText} src={item.iconPath} />;
    };

    return (
        <Section id='product-page-benefits-card' style={{ marginTop: `${props.topMargin || 0}`, marginBottom: `${props.bottomMargin || 0}` }}
            className={`${iconClasses.section[iconClassIndex]} ${grayBackgroundClass} !py-0`}
            containerClass={`${iconClasses.container[iconClassIndex]}`}
            containerFluid={props.fluidContainer}>
            <div className={`content ${boxShadowClassImage} py-12`}>
                <Title 
                    className='mb-6'
                    verticalBar={props.version === Version.Icon && props.newDesign}
                    showHr={props.version === Version.Image || !props.newDesign}
                    tag='h2'
                    align='center'
                >
                    {transformHTMLParserLinks(props.title ?? '')}
                </Title>
                {props.subTitle &&
                    <p className='ProductPageBenefits__subTitle'>{transformHTMLParserLinks(props.subTitle)}</p>
                }
                <div className='ProductPageBenefits__benefit mb-6'>
                    {props.version === 'icon' ?
                        <Row className={iconClasses.iconVersion[iconClassIndex]} xs={1} md={3} justify='center'>
                            {props.benefits && props.benefits[currentIndex] &&
                                <CardBenefitModal
                                    modalIsOpen={isModalOpen}
                                    setModalIsOpen={setIsModalOpen}
                                    benefitItem={props.benefits![currentIndex]}
                                    version={props.version}
                                />
                            }

                            {props.benefits && props.benefits.length > 0 && props.benefits.map((benefitItem, index) => {
                                return (
                                    <Col key={index} className={`${iconClasses.benefitItemColumn[iconClassIndex]} ${boxShadowClassIcon}`}>
                                        <button
                                            className={`${iconClasses.benefitItem[iconClassIndex]}` + ((isModalOpen && index === currentIndex) ? ' ProductPageBenefits__benefitItem--open' : '')}
                                            onClick={() => {
                                                setCurrentIndex(index);
                                                setIsModalOpen(true);
                                            }}>
                                            {getBenefitItem(benefitItem)}
                                            {benefitItem.title &&
                                                <div className={iconClasses.itemTitle[iconClassIndex]}>
                                                    {transformHTMLParserLinks(benefitItem.title)}
                                                    {!props.newDesign && <IconChevron size='small' className='ProductPageBenefits__itemTitle--chevron' />}
                                                </div>
                                            }
                                        </button>
                                    </Col>
                                );
                            })}
                        </Row>
                        :
                        <Row className='ProductPageBenefits__imageVersionRow mt-4'>
                            {props.benefits && props.benefits.map((benefitItem, index) => {
                                return (
                                    <Col key={index} className='ProductPageBenefits__imageVersionColumn'>
                                        {getBenefitItem(benefitItem)}
                                        {benefitItem.title &&
                                            <div className='ProductPageBenefits__itemTitleBold'>
                                                {transformHTMLParserLinks(benefitItem.title)}
                                            </div>
                                        }
                                    </Col>
                                );
                            })}
                        </Row>
                    }
                </div>
            </div>
        </Section>
    );
};

export default ProductPageBenefitsCard;
